import { useEffect, useMemo, useState } from 'react';
import { Card, Checkbox, Row, Col, Typography, Button, Empty, Tooltip, InputNumber } from 'antd';
import { CloseOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { findingTypes, resolveColor, dentalInputCodes, gingivalInterpretation } from './odontogramConfig';
import { getSurfaceLabel } from './ToothDiagram';
import { computeOdontogramCounts } from './odontogramUtils';

const { Text } = Typography;

const resolveLabel = (finding) => findingTypes[finding.type]?.label || finding.type;

const flattenFindings = (toothFindings) => {
  const rows = [];
  Object.entries(toothFindings).forEach(([num, findings]) =>
    findings.forEach((finding, index) => rows.push({ toothNum: +num, findingIdx: index, ...finding }))
  );
  return rows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
};

const groupByCondition = (toothFindings) => {
  const map = new Map();
  Object.entries(toothFindings).forEach(([num, findings]) => {
    findings.forEach((finding) => {
      if (!map.has(finding.type)) {
        map.set(finding.type, { label: resolveLabel(finding), color: resolveColor(finding), teethSet: new Set() });
      }
      map.get(finding.type).teethSet.add(+num);
    });
  });
  return [...map.values()]
    .map(({ teethSet, ...rest }) => ({
      ...rest,
      count: teethSet.size,
      teeth: [...teethSet].sort((a, b) => a - b),
    }))
    .sort((a, b) => b.count - a.count);
};



const SummaryCard = ({ title, extra, children }) => (
  <Card
    size="small"
    title={<span className="odo-clinical-summary-card-title">{title}</span>}
    extra={extra}
    className="odo-clinical-summary-card"
  >
    {children}
  </Card>
);

const StateCard = ({ value, onChange, disabled }) => (
  <SummaryCard title="Estado General">
    <Row gutter={6}>
      {['Normal', 'Alterado'].map(option => (
        <Col key={option} span={12}>
          <button
            type="button"
            onClick={() => !disabled && onChange(option)}
            disabled={disabled}
            className={`odo-clinical-summary-estado-btn${value === option ? ` odo-clinical-summary-estado-btn--${option.toLowerCase()}` : ''}`}
          >
            {option}
          </button>
        </Col>
      ))}
    </Row>
  </SummaryCard>
);

const cpodCells = [
  { key: 'cpodC', procCode: dentalInputCodes.cpodC, label: 'Cariados', abbreviation: 'C' },
  { key: 'cpodP', procCode: dentalInputCodes.cpodP, label: 'Perdidos', abbreviation: 'P' },
  { key: 'cpodO', procCode: dentalInputCodes.cpodO, label: 'Obturados', abbreviation: 'O' },
  { key: 'cpodTotal', procCode: dentalInputCodes.cpodTotal, label: 'CPOD', abbreviation: 'CPO' },
];

const CPODCard = ({ cpod, formValues = {}, onChange, disabled, cpodExcludedTeeth = [], onToggleCpodExclusion }) => {
  const [showExcluded, setShowExcluded] = useState(false);
  return (
  <SummaryCard title="Índice CPOD">
    <Row gutter={4}>
      {cpodCells.map(({ key, procCode, label, abbreviation }) => {
        const computed = cpod[key] ?? 0;
        const override = formValues[procCode];
        const displayValue = override !== undefined ? override : computed;
        const isTotal = abbreviation === 'CPO';
        return (
          <Col key={key} span={6}>
            <div className={`odo-clinical-summary-cpod-cell${isTotal ? ' odo-clinical-summary-cpod-cell--total' : ''}`}>
              <input
                type="number"
                min={0}
                max={28}
                className="odo-clinical-summary-cpod-input"
                value={displayValue}
                disabled={disabled}
                onFocus={event => event.target.select()}
                onChange={event => {
                  const raw = event.target.value;
                  if (raw === '') { onChange?.(procCode, 0); return; }
                  onChange?.(procCode, Number(raw));
                }}
              />
              <div className="odo-clinical-summary-cpod-label">{label}</div>
              <div className="odo-clinical-summary-cpod-key">{abbreviation}</div>
            </div>
          </Col>
        );
      })}
    </Row>
    {cpodExcludedTeeth.length > 0 && (
      <div className="odo-cpod-excluded-section">
        <button
          type="button"
          className="odo-cpod-excluded-toggle"
          onClick={() => setShowExcluded(previousValue => !previousValue)}
        >
          {showExcluded ? <DownOutlined /> : <RightOutlined />}
          <span>Excluidas ({cpodExcludedTeeth.length})</span>
        </button>
        {showExcluded && (
          <div className="odo-cpod-excluded-list">
            {[...cpodExcludedTeeth].sort((a, b) => a - b).map(tooth => (
              <span key={tooth} className="odo-cpod-excluded-tag">
                {tooth}
                {!disabled && onToggleCpodExclusion && (
                  <CloseOutlined onClick={() => onToggleCpodExclusion(tooth)} />
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    )}
  </SummaryCard>
  );
};

const GingivalIndexCard = ({ formValues, onChange, disabled, proceduresCodes }) => {
  const gingivalValue = formValues[proceduresCodes?.gingivalValue];

  const num = parseFloat(gingivalValue);
  const interpretation = !Number.isNaN(num) ? gingivalInterpretation(num) : null;

  const interpretationLabel = interpretation?.label || '';

  useEffect(() => {
    if (gingivalValue === undefined || gingivalValue === null) return;
    onChange?.(proceduresCodes.gingivalInterpretation, interpretationLabel);
  }, [gingivalValue, interpretationLabel, onChange, proceduresCodes.gingivalInterpretation]);

  return (
    <SummaryCard title="Índice Gingival">
      <Row align="middle" gutter={8}>
        <Col span={10}>
          <InputNumber
            min={0}
            max={3}
            step={0.01}
            size="small"
            className="odo-clinical-summary-gi-input"
            value={gingivalValue}
            disabled={disabled}
            onChange={value => onChange?.(proceduresCodes.gingivalValue, value)}
            placeholder="0.00"
          />
        </Col>
        <Col span={14}>
          {interpretation
            ? <div className="odo-clinical-summary-gi-badge" style={{ background: interpretation.bg, borderColor: interpretation.color, color: interpretation.color, border: `1px solid ${interpretation.color}` }}>
              {interpretation.label.toUpperCase()}
            </div>
            : <span className="odo-clinical-summary-empty-text">Sin registro</span>
          }
        </Col>
      </Row>
    </SummaryCard>
  );
};

const BacterialPlaqueCard = ({ softPlaque, calcifiedPlaque, onChange, disabled }) => (
  <SummaryCard title="Placa Bacteriana">
    <Row gutter={6}>
      <Col span={12}>
        <Checkbox
          checked={softPlaque === 'si'}
          onChange={() => !disabled && onChange(dentalInputCodes.softPlaque, softPlaque === 'si' ? 'no' : 'si')}
          disabled={disabled}
          className="odo-clinical-summary-checkbox"
        >
          Placa Blanda
        </Checkbox>
      </Col>
      <Col span={12}>
        <Checkbox
          checked={calcifiedPlaque === 'si'}
          onChange={() => !disabled && onChange(dentalInputCodes.calcifiedPlaque, calcifiedPlaque === 'si' ? 'no' : 'si')}
          disabled={disabled}
          className="odo-clinical-summary-checkbox"
        >
          Placa Calcificada
        </Checkbox>
      </Col>
    </Row>
  </SummaryCard>
);

const ConditionSummaryCard = ({ toothFindings }) => {
  const grouped = useMemo(() => groupByCondition(toothFindings), [toothFindings]);
  return (
    <SummaryCard title="Resumen por Condición">
      <div className="odo-clinical-summary-cond-scroll">
        {grouped.length === 0
          ? <Text className="odo-clinical-summary-empty-text">Sin condiciones</Text>
          : <>
            <Row className="odo-clinical-summary-table-header" align="middle" wrap={false}>
              <Col className="odo-clinical-summary-col-condition"><span>Condición</span></Col>
              <Col className="odo-clinical-summary-col-count"><span>Cant.</span></Col>
              <Col className="odo-clinical-summary-col-teeth"><span>Piezas</span></Col>
            </Row>
            {grouped.map(({ label, count, teeth, color }) => (
              <Row key={label} className="odo-clinical-summary-cond-row" align="middle" wrap={false}>
                <Col className="odo-clinical-summary-col-condition">
                  <span className="odo-clinical-summary-condition-name" style={{ color: color || undefined }}>{label}</span>
                </Col>
                <Col className="odo-clinical-summary-col-count"><strong className="odo-clinical-summary-count-num">{count}</strong></Col>
                <Col className="odo-clinical-summary-col-teeth">
                  <span className="odo-clinical-summary-teeth-list">{teeth.sort((a, b) => a - b).join(' · ')}</span>
                </Col>
              </Row>
            ))}
          </>
        }
      </div>
    </SummaryCard>
  );
};

const FindingsCard = ({ toothFindings, multiToothFindings = [], onRemove, onRemoveMultiFinding, onClearAll, disabled }) => {
  const findings = useMemo(() => flattenFindings(toothFindings), [toothFindings]);
  return (
    <SummaryCard
      title={`Hallazgos (${findings.length + multiToothFindings.length})`}
      extra={
        findings.length > 0 && !disabled
          ? <Button size="small" type="link" danger onClick={onClearAll} className="odo-clinical-summary-clear-btn">Limpiar todo</Button>
          : null
      }
    >
      <div className="odo-clinical-summary-findings-scroll">
        {findings.length === 0 && multiToothFindings.length === 0
          ? <Empty description={<span className="odo-clinical-summary-empty-text">Sin hallazgos</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          : <>
            <Row className="odo-clinical-summary-table-header" align="middle" wrap={false}>
              <Col className="odo-clinical-summary-col-tooth"><span>Pieza</span></Col>
              <Col className="odo-clinical-summary-col-condition"><span>Condición</span></Col>
              <Col className="odo-clinical-summary-col-surface"><span>Superficie</span></Col>
              <Col className="odo-clinical-summary-col-action" />
            </Row>
            <div className="odo-clinical-summary-findings-list">
              {findings.map(({ toothNum, findingIdx, type, label, surface, color }) => (
                <Row key={`${toothNum}-${findingIdx}`} className="odo-clinical-summary-finding-row" align="middle" wrap={false}>
                  <Col className="odo-clinical-summary-col-tooth"><strong>{toothNum}</strong></Col>
                  <Col className="odo-clinical-summary-col-condition">
                    <Tooltip title={resolveLabel({ type, label })} placement="left">
                      <span className="odo-clinical-summary-condition-name" style={{ color: resolveColor({ type, color }) || undefined }}>
                        {resolveLabel({ type, label })}
                      </span>
                    </Tooltip>
                  </Col>
                  <Col className="odo-clinical-summary-col-surface">
                    <span className="odo-clinical-summary-surface-text">{surface ? (getSurfaceLabel(toothNum, surface) || surface) : '—'}</span>
                  </Col>
                  <Col className="odo-clinical-summary-col-action">
                    {!disabled && <CloseOutlined className="odo-clinical-summary-remove-btn" onClick={() => onRemove(toothNum, findingIdx)} />}
                  </Col>
                </Row>
              ))}
              {multiToothFindings.length > 0 && (
                <>
                  <div className="odo-clinical-summary-multi-divider">
                    Multi-pieza
                  </div>
                  {multiToothFindings.map((mf, idx) => (
                    <Row key={`multi-${idx}`} className="odo-clinical-summary-finding-row" align="middle" wrap={false}>
                      <Col className="odo-clinical-summary-col-tooth"><strong>{mf.fromTooth}→{mf.toTooth}</strong></Col>
                      <Col className="odo-clinical-summary-col-condition">
                        <span className="odo-clinical-summary-condition-name" style={{ color: resolveColor(mf) || undefined }}>
                          {resolveLabel(mf)}
                        </span>
                      </Col>
                      <Col className="odo-clinical-summary-col-surface"><span className="odo-clinical-summary-surface-text">—</span></Col>
                      <Col className="odo-clinical-summary-col-action">
                        {!disabled && <CloseOutlined className="odo-clinical-summary-remove-btn" onClick={() => onRemoveMultiFinding(idx)} />}
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            </div>
          </>
        }
      </div>
    </SummaryCard>
  );
};

const ClinicalSummary = ({
  toothFindings = {}, multiToothFindings = [], formValues = {},
  onChange, onRemoveFinding, onRemoveMultiFinding, onClearAll, disabled = false,
  proceduresCodes,
  compact = false,
  cpodExcludedTeeth = [],
  onToggleCpodExclusion,
}) => {
  const cpod = useMemo(() => computeOdontogramCounts(toothFindings, multiToothFindings, cpodExcludedTeeth), [toothFindings, multiToothFindings, cpodExcludedTeeth]);

  if (compact) {
    return (
      <div className="odo-clinical-summary odo-clinical-summary--compact">
        <Row gutter={[8, 4]} wrap={false}>
          <Col flex="3">
            <StateCard value={formValues[dentalInputCodes.dentalStatus] || null} onChange={value => onChange?.(dentalInputCodes.dentalStatus, value)} disabled={disabled} />
          </Col>
          <Col flex="4">
            <CPODCard cpod={cpod} formValues={formValues} onChange={onChange} disabled={disabled} cpodExcludedTeeth={cpodExcludedTeeth} onToggleCpodExclusion={onToggleCpodExclusion} />
          </Col>
          <Col flex="3">
            <GingivalIndexCard toothFindings={toothFindings} formValues={formValues} onChange={onChange} disabled={disabled} proceduresCodes={proceduresCodes} />
          </Col>
          <Col flex="2">
            <BacterialPlaqueCard softPlaque={formValues[dentalInputCodes.softPlaque]} calcifiedPlaque={formValues[dentalInputCodes.calcifiedPlaque]} onChange={onChange} disabled={disabled} />
          </Col>
        </Row>

        <Row gutter={[8, 4]} className="odo-clinical-summary-compact-row2">
          <Col span={12}>
            <ConditionSummaryCard toothFindings={toothFindings} />
          </Col>
          <Col span={12}>
            <FindingsCard toothFindings={toothFindings} multiToothFindings={multiToothFindings} onRemove={onRemoveFinding} onRemoveMultiFinding={onRemoveMultiFinding} onClearAll={onClearAll} disabled={disabled} />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="odo-clinical-summary">
      <Row gutter={[8, 4]}>
        <Col xs={24} md={12} xl={24}>
          <StateCard value={formValues[dentalInputCodes.dentalStatus] || null} onChange={value => onChange?.(dentalInputCodes.dentalStatus, value)} disabled={disabled} />
        </Col>
        <Col xs={24} md={12} xl={24}>
          <CPODCard cpod={cpod} formValues={formValues} onChange={onChange} disabled={disabled} cpodExcludedTeeth={cpodExcludedTeeth} onToggleCpodExclusion={onToggleCpodExclusion} />
        </Col>
        <Col xs={24} md={12} xl={24}>
          <GingivalIndexCard toothFindings={toothFindings} formValues={formValues} onChange={onChange} disabled={disabled} proceduresCodes={proceduresCodes} />
        </Col>
        <Col xs={24} md={12} xl={24}>
          <BacterialPlaqueCard softPlaque={formValues[dentalInputCodes.softPlaque]} calcifiedPlaque={formValues[dentalInputCodes.calcifiedPlaque]} onChange={onChange} disabled={disabled} />
        </Col>
        <Col xs={24} md={24} xl={24}>
          <ConditionSummaryCard toothFindings={toothFindings} />
        </Col>
        <Col xs={24} md={24} xl={24}>
          <FindingsCard toothFindings={toothFindings} multiToothFindings={multiToothFindings} onRemove={onRemoveFinding} onRemoveMultiFinding={onRemoveMultiFinding} onClearAll={onClearAll} disabled={disabled} />
        </Col>
      </Row>
    </div>
  );
};

export default ClinicalSummary;
